import { ACCOUNT_CATEGORY, THEME } from "@toruslabs/base-controllers";
import log from "loglevel";
import { defineStore } from "pinia";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import useUtils from "../composables/use-utils";
import AuthFactory from "../handlers/Auth/AuthFactory";
import { SOLANA_WALLET_DAPP_NAME } from "../utils/constants";
import { ROUTES, TORUS_CONTROLLER_EVENTS } from "../utils/enums";
import { LOGIN_MODE } from "../utils/interfaces";
import { isMain, setThemeColors } from "../utils/utils";
import { useControllerStore } from "./controllerStore";

export const useAuthStore = defineStore("auth", () => {
  const controllerStore = useControllerStore();
  const { setLoginMode, sessionNamespace, loginMode } = useUtils();
  const router = useRouter();
  const route = useRoute();
  const loader = ref(false);

  async function logout() {
    controllerStore.logout();

    await AuthFactory.getInstance({
      web3AuthClientId: controllerStore.torusCtrl.getWeb3AuthClientId(),
      web3AuthNetwork: controllerStore.torusCtrl.getWeb3AuthNetwork(),
      rehydrate: false,
      sessionNamespace: sessionNamespace.value,
      replaceUrlOnRedirect: true,
    });
    try {
      await AuthFactory.invalidateSession();
    } catch (error) {
      log.warn(error, "unable to logout with auth service");
      if (isMain) window.location.href = "/";
    }
    if (isMain) router.push({ name: ROUTES.LOGIN });
  }

  async function rehydrate(initController = false, sessionIdentifier: string = "", replaceUrlOnRedirect = true) {
    try {
      loader.value = true;
      await router.isReady();
      if (!route.meta.skipAuthCheck) {
        // do only on certain routes
        if (initController)
          await controllerStore.init({
            origin: window.location.origin,
          });
        controllerStore.torusCtrl.on(TORUS_CONTROLLER_EVENTS.LOGOUT, () => {
          logout();
        });

        // user is already logged in. no need to do whitelabel for auth
        await controllerStore.setUserTheme(controllerStore.userTheme as THEME);
        setThemeColors(controllerStore.whiteLabelTheme);
        const authInstance = await AuthFactory.getInstance({
          web3AuthClientId: controllerStore.torusCtrl.getWeb3AuthClientId(),
          web3AuthNetwork: controllerStore.torusCtrl.getWeb3AuthNetwork(),
          rehydrate: true,
          sessionId: sessionIdentifier,
          sessionNamespace: sessionNamespace.value,
          replaceUrlOnRedirect,
        });
        const { sessionId, state: authState } = authInstance;
        if (!sessionId) {
          controllerStore.logout();
          if (isMain) await router.push({ name: ROUTES.LOGIN });
          return;
        }
        // set the http client for the controller
        controllerStore.torusCtrl.setHttpClient(authInstance);
        // we get the sessionIdentifier from the url
        // which is only present in the mobile sdks.
        if (sessionIdentifier && sessionIdentifier === sessionId) setLoginMode(LOGIN_MODE.mobile_sdk);

        // Process keys
        if (authState.walletKey || authState.tKey || authState.privKey) {
          // const walletKey = AuthFactory.getWalletKey();
          const { keys, signatures, postboxKey } = await AuthFactory.getKeysInfo();
          const userInfo = await AuthFactory.getUserInfo();

          log.info("auto-login with auth service session");

          // Main Accounts
          const mainAddresses = await Promise.all(
            keys.map(async (key) => {
              const address = await controllerStore.torusCtrl.addAccount({
                privKey: key.privKey.padStart(64, "0"),
                userInfo,
                rehydrate: true,
                type: key.accountType,
                signatures,
              });

              return {
                accountType: key.accountType,
                address,
              };
            })
          );

          let selectedAddress =
            controllerStore.torusState.PreferencesControllerState.selectedAddress || mainAddresses.filter((x) => !!x.address)[0].address;

          // Dashboard Accounts

          const thresholdAccount = mainAddresses.find((key) => key.accountType === ACCOUNT_CATEGORY.THRESHOLD);
          if ((loginMode.value === LOGIN_MODE.main || loginMode.value === LOGIN_MODE.embed) && thresholdAccount) {
            const { userDapps, keys: extraKeys } = await AuthFactory.getUserDapps({ postboxKey, chainNamespace: controllerStore.chainNamespace, httpClient: controllerStore.torusCtrl.httpClient });

            await Promise.all(
              extraKeys.map(async (key) => {
                const accountType =
                  userDapps[key.address] && key.accountType === ACCOUNT_CATEGORY.APP_SCOPED ? ACCOUNT_CATEGORY.APP_SCOPED_DERIVED : key.accountType;

                const address = await controllerStore.torusCtrl.addAccount({
                  privKey: key.privKey.padStart(64, "0"),
                  userInfo: {
                    ...userInfo,
                    name: userDapps[key.address] || userInfo.name,
                  },
                  rehydrate: true,
                  type: accountType,
                  signatures,
                  mainAddress: thresholdAccount?.address,
                });

                if (userDapps[key.address] === SOLANA_WALLET_DAPP_NAME) {
                  selectedAddress = address;
                }
              })
            );
          }

          controllerStore.torusCtrl.setSelectedAccount(selectedAddress);

          // check user skipped mfa
          controllerStore.setIsUserSkippedMfa(AuthFactory.isUserSkippedMfa(keys));
          await controllerStore.ensureAllowedChain();

          if (!isMain) controllerStore.torusCtrl.notifyIsLoggedIn();
        } else {
          log.info("no auth session");
        }
      }
    } catch (error) {
      log.error(error, "Unable to rehydrate auth instance");
    } finally {
      loader.value = false;
    }
  }

  function setLoaderState(loading: boolean) {
    loader.value = loading;
  }

  return { rehydrate, logout, loader, setLoaderState };
});
